//shadi-arts-portfolio\client\src\components\Gallery.jsx

import React, { useState, useEffect, useRef } from "react";
import "./Gallery.css";
import { useOrder } from "../context/OrderContext";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { useLanguage } from "../context/LanguageContext";
import { analytics } from "../firebase";
import { logEvent } from "firebase/analytics";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { trackEvent } from "../utils/analytics";

// بيانات الأعمال التجريبية (mock data) - ستتم إزالتها لاحقاً
const mockArtworks = [];

const Gallery = () => {
  const { t, language } = useLanguage();

  // Category translation mapping
  const getTranslatedCategory = (category) => {
    const translations = {
      All: t("All", "الكل"),
      couples: t("Couples", "أزواج"),
      families: t("Families", "عائلات"),
      children: t("Children", "أطفال"),
      elders: t("Elders", "كبار السن"),
      unknown: t("Unknown", "مجهول"),
      actors: t("Actors", "نجوم التمثيل"),
      singers: t("Singers", "نجوم الغناء"),
      icons: t("Icons", "شخصيات ملهمة"),
      Pets: t("Pets", "حيوانات أليفة"),
      "sport-legends": t("Sport Legends", "أساطير الرياضة"),
      "media-personalities": t("Media Personalities", "شخصيات إعلامية"),
      "movie-posters": t("Movie Posters", "بوسترات أفلام"),
    };
    return translations[category] || category;
  };

  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [filterChange, setFilterChange] = useState(false);
  const galleryRef = useRef(null);
  const cardRefs = useRef([]);

  // Add the order context hook
  const { addReferenceImage, clearReference } = useOrder();

  // جلب الأعمال الحقيقية من Firestore + دمجها مع البيانات التجريبية
  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        const snapshot = await getDocs(collection(db, "artworks"));
        const firestoreArtworks = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          isMock: false,
        }));

        const allArtworks = [...firestoreArtworks, ...mockArtworks];
        setArtworks(
          allArtworks.sort((a, b) => (a.order || 0) - (b.order || 0)),
        );
      } catch (error) {
        console.error("Error fetching artworks from Firestore:", error);
        setArtworks(mockArtworks);
      } finally {
        setLoading(false);
      }
    };

    fetchArtworks();
  }, []);

  // Dynamic categories list from combined artworks
  const categories = ["All", ...new Set(artworks.map((art) => art.category))];

  const filteredArtworks =
    selectedCategory === "All"
      ? artworks
      : artworks.filter((art) => art.category === selectedCategory);

  const handleCategoryClick = (category) => {
    setFilterChange(true);
    setSelectedCategory(category);

    cardRefs.current.forEach((card) => {
      if (card) {
        card.classList.add("filter-change");
        setTimeout(() => {
          card.classList.remove("filter-change");
        }, 600);
      }
    });

    setTimeout(() => {
      setFilterChange(false);
    }, 300);
  };

  const handleArtworkClick = (artwork) => {
    setSelectedArtwork(artwork);

    logEvent(analytics, "artwork_click", {
      artwork_title: artwork.title,
      artwork_category: artwork.category,
      artwork_id: artwork.id,
    });

    trackEvent("artwork_click", {
      artworkTitle: artwork.title,
      artworkCategory: artwork.category,
    });

    const index = artworks.findIndex((a) => a.id === artwork.id);

    const card = cardRefs.current[index];
    if (card) {
      card.style.transform = "scale(0.98)";
      setTimeout(() => {
        card.style.transform = "";
      }, 150);
    }
  };

  const closeModal = () => {
    setSelectedArtwork(null);
  };

  // Handle Order Similar Portrait click [first function button]
  const handleOrderSimilar = () => {
    if (selectedArtwork) {
      logEvent(analytics, "order_similar_click", {
        artwork_title: selectedArtwork.title,
        artwork_category: selectedArtwork.category,
      });

      trackEvent("order_similar_click", {
        artworkTitle: selectedArtwork.title,
        artworkCategory: selectedArtwork.category,
      });

      addReferenceImage({
        imageUrl: selectedArtwork.imageUrl,
        title: selectedArtwork.title,
        category: selectedArtwork.category,
        description: selectedArtwork.description,
        size: selectedArtwork.size,
        tags: selectedArtwork.tags,
      });
      closeModal();

      setTimeout(() => {
        const orderSection = document.getElementById("order");
        if (orderSection) {
          orderSection.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    }
  };

  // Handle Cutom Order click [second function button]
  const handleCustomOrder = () => {
    closeModal();

    clearReference();

    setTimeout(() => {
      const orderSection = document.getElementById("order");
      if (orderSection) {
        orderSection.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
  };

  // 🆕 Watch Video Handlers (Long & Short)
  const handleWatchLongVideo = () => {
    if (selectedArtwork?.longVideoId) {
      window.open(
        `https://www.youtube.com/watch?v=${selectedArtwork.longVideoId}`,
        "_blank",
        "noopener,noreferrer",
      );

      logEvent(analytics, "watch_video_click", {
        artwork_title: selectedArtwork.title,
        video_type: "long",
        video_id: selectedArtwork.longVideoId,
      });

      trackEvent("watch_video_click", {
        artworkTitle: selectedArtwork.title,
        videoType: "long",
      });
    }
  };

  const handleWatchShortVideo = () => {
    if (selectedArtwork?.shortVideoId) {
      window.open(
        `https://www.youtube.com/shorts/${selectedArtwork.shortVideoId}`,
        "_blank",
        "noopener,noreferrer",
      );

      logEvent(analytics, "watch_video_click", {
        artwork_title: selectedArtwork.title,
        video_type: "short",
        video_id: selectedArtwork.shortVideoId,
      });

      trackEvent("watch_video_click", {
        artworkTitle: selectedArtwork.title,
        videoType: "short",
      });
    }
  };

  // ==================== 🔒 حماية الصور - ENHANCED VERSION ====================
  useEffect(() => {
    const styleId = "image-protection-global";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        .real-portrait-image, 
        .modal-real-image,
        .artwork-real-img {
          pointer-events: none !important;
          -webkit-user-drag: none !important;
          user-drag: none !important;
          user-select: none !important;
          -webkit-user-select: none !important;
        }
        
        body {
          user-select: none !important;
          -webkit-user-select: none !important;
        }
        
        input, textarea, select, button {
          user-select: text !important;
          -webkit-user-select: text !important;
        }
        
        .gallery-protection-toast {
          position: fixed;
          bottom: 20px;
          right: 20px;
          background: #1a1a1a;
          color: #f0a500;
          padding: 12px 18px;
          border-radius: 8px;
          border: 1px solid #f0a500;
          z-index: 99999;
          font-weight: bold;
          display: none;
          max-width: 300px;
          font-size: 0.85rem;
          box-shadow: 0 5px 15px rgba(0,0,0,0.3);
        }
        
        .gallery-protection-toast.show {
          display: block;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `;
      document.head.appendChild(style);
    }

    const showProtectionAlert = (type) => {
      const oldToast = document.getElementById("gallery-protection-toast");
      if (oldToast) oldToast.remove();

      const toast = document.createElement("div");
      toast.id = "gallery-protection-toast";
      toast.className = "gallery-protection-toast";

      const message =
        type === "right-click"
          ? "🛡️ الصورة محمية © Shadi Arts - للاستفسارات: +20 1151638804"
          : type === "screenshot"
            ? "🛡️ تم تعطيل لقطة الشاشة حمايةً للحقوق الفنية"
            : "📋 المحتوى محمي بموجب حقوق النشر © Shadi Arts";

      toast.textContent = message;
      document.body.appendChild(toast);
      setTimeout(() => toast.classList.add("show"), 10);
      setTimeout(() => {
        toast.classList.remove("show");
        setTimeout(() => {
          if (toast.parentNode) toast.remove();
        }, 300);
      }, 3000);
    };

    const handleRightClick = (e) => {
      if (
        e.target.tagName === "IMG" ||
        e.target.closest(".real-image-container")
      ) {
        e.preventDefault();
        showProtectionAlert("right-click");
        return false;
      }
    };

    const handleKeyPress = (e) => {
      if (
        (e.ctrlKey &&
          (e.key === "s" || e.key === "S" || e.key === "u" || e.key === "U")) ||
        (e.ctrlKey &&
          e.shiftKey &&
          (e.key === "I" || e.key === "i" || e.key === "C" || e.key === "c")) ||
        e.key === "F12"
      ) {
        e.preventDefault();
        showProtectionAlert("screenshot");
        return false;
      }
    };

    const handlePrintScreen = (e) => {
      if (e.key === "PrintScreen") {
        e.preventDefault();
        showProtectionAlert("screenshot");
        navigator.clipboard.writeText("Image protected by Shadi Arts ©");
        return false;
      }
    };

    const handleDragStart = (e) => {
      if (e.target.tagName === "IMG") {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener("contextmenu", handleRightClick);
    document.addEventListener("keydown", handleKeyPress);
    document.addEventListener("keyup", handlePrintScreen);
    document.addEventListener("dragstart", handleDragStart);

    return () => {
      document.removeEventListener("contextmenu", handleRightClick);
      document.removeEventListener("keydown", handleKeyPress);
      document.removeEventListener("keyup", handlePrintScreen);
      document.removeEventListener("dragstart", handleDragStart);
      const toast = document.getElementById("gallery-protection-toast");
      if (toast) toast.remove();
      const style = document.getElementById("image-protection-global");
      if (style) style.remove();
    };
  }, []);

  // إضافة reveal on scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: "0px 0px -50px 0px",
      },
    );

    const elements = document.querySelectorAll(".reveal-on-scroll");
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // عرض شاشة تحميل أثناء جلب البيانات
  if (loading) {
    return (
      <section className="gallery" id="gallery">
        <div className="gallery-container">
          <div className="gallery-header">
            <h2 className="gallery-title">
              <span className="title-underline">
                {" "}
                {t("Portfolio", "معرض ")}{" "}
              </span>{" "}
              {t("Gallery", " اعمالي")}
            </h2>
            <p className="gallery-subtitle">
              {" "}
              {t("Loading artworks...", "تحميل اللوحات  ...")}{" "}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="gallery reveal-on-scroll" id="gallery" ref={galleryRef}>
      <div className="gallery-container">
        <div className="gallery-header reveal-on-scroll">
          <h2 className="gallery-title">
            <span className="title-underline">{t("Portfolio", "معرض ")}</span>{" "}
            {t("Gallery", " اعمالي")}
          </h2>
          <p className="gallery-subtitle">
            {t(
              "A collection of hand-drawn pencil portraits, each telling a unique story",
              "مجموعة من البورتريهات المرسومة يدويا باقلم الرصاص ,لكل منها قصة فريدة",
            )}
          </p>
        </div>

        {/* فلاتر التصنيف */}
        <div className="category-filters reveal-on-scroll">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-btn ${selectedCategory === category ? "active" : ""} floating`}
              onClick={() => handleCategoryClick(category)}
            >
              {getTranslatedCategory(category)}
              <span className="btn-underline"></span>
            </button>
          ))}
        </div>

        {/* شبكة الصور */}
        <div className="artworks-grid">
          {filteredArtworks.map((artwork, index) => (
            <div
              key={artwork.id}
              className={`artwork-card ${filterChange ? "filter-change" : ""}`}
              onClick={() => handleArtworkClick(artwork)}
              ref={(el) => (cardRefs.current[index] = el)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="artwork-image shimmer">
                {artwork.imageUrl ? (
                  <div className="real-image-container">
                    <img
                      src={artwork.imageUrl}
                      alt={artwork.title}
                      className="real-portrait-image protected-image"
                      onError={(e) => {
                        e.target.style.display = "none";
                        const placeholder = e.target.nextElementSibling;
                        if (placeholder) placeholder.style.display = "block";
                      }}
                    />
                    <div
                      className="placeholder-art"
                      style={{ display: "none" }}
                    >
                      <div className="art-preview">
                        <div className="pencil-stroke stroke-1"></div>
                        <div className="pencil-stroke stroke-2"></div>
                        <div className="pencil-stroke stroke-3"></div>
                        <div className="artwork-overlay">
                          <span className="view-text">
                            👁️ {t("View Details", "مشاهدة التفاصيل")}{" "}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="placeholder-art">
                    <div className="art-preview">
                      <div className="pencil-stroke stroke-1"></div>
                      <div className="pencil-stroke stroke-2"></div>
                      <div className="pencil-stroke stroke-3"></div>
                      <div className="artwork-overlay">
                        <span className="view-text">
                          👁️ {t("View Details", "مشاهدة التفاصيل")}{" "}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="category-tag floating">
                  {getTranslatedCategory(artwork.category)}
                </div>
              </div>

              <div className="artwork-info">
                <h3 className="artwork-title">
                  {language === "ar"
                    ? artwork.arabicTitle || artwork.title
                    : artwork.title}
                </h3>
                <p className="artwork-description">
                  {language === "ar"
                    ? artwork.arabicDescription || artwork.description
                    : artwork.description}
                </p>

                <div className="artwork-meta">
                  <span className="meta-item">
                    <span className="meta-icon">📅</span>
                    {artwork.year}
                  </span>
                  <span className="meta-item">
                    <span className="meta-icon">📏</span>
                    {artwork.size}
                  </span>
                </div>

                <div className="artwork-tags">
                  {artwork.tags.map((tag) => (
                    <span key={tag} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ملاحظة للمستخدم */}
        <div className="gallery-note reveal-on-scroll">
          <div className="note-icon">💡</div>
          <p className="note-text">
            <strong> {t("Note", "ملاحظة")} :</strong>{" "}
            {t(" These are sample artworks ", "هذه عينات من اعمالي الفنية ")} .
            {t(
              "Each portrait takes approximately at least 4 hours or more",
              "كل بورتريه يستغرق تقريبا ما لا يقل عن 4 ساعات  او اكثر ",
            )}{" "}
            .
          </p>
        </div>
      </div>

      {/* Modal */}
      {selectedArtwork && (
        <div className="artwork-modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              ×
            </button>

            <div className="modal-body">
              <div className="modal-image">
                {selectedArtwork.imageUrl ? (
                  <div className="modal-real-image-container">
                    <TransformWrapper
                      initialScale={1}
                      minScale={1}
                      maxScale={4}
                      centerOnInit={true}
                    >
                      {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                        <>
                          <div className="zoom-controls">
                            <button
                              onClick={() => zoomIn()}
                              className="zoom-btn"
                              title="تكبير"
                            >
                              ➕
                            </button>
                            <button
                              onClick={() => zoomOut()}
                              className="zoom-btn"
                              title="تصغير"
                            >
                              ➖
                            </button>
                            <button
                              onClick={() => resetTransform()}
                              className="zoom-btn"
                              title="إعادة تعيين"
                            >
                              ⟳
                            </button>
                          </div>
                          <TransformComponent>
                            <img
                              src={selectedArtwork.imageUrl}
                              alt={selectedArtwork.title}
                              className="modal-real-image protected-image"
                              style={{
                                width: "100%",
                                height: "auto",
                                cursor: "grab",
                              }}
                            />
                          </TransformComponent>
                        </>
                      )}
                    </TransformWrapper>

                    <div
                      className="modal-placeholder shimmer"
                      style={{ display: "none" }}
                    >
                      <div className="modal-preview">
                        <div className="modal-stroke stroke-1"></div>
                        <div className="modal-stroke stroke-2"></div>
                        <div className="modal-stroke stroke-3"></div>
                        <div className="modal-stroke stroke-4"></div>
                      </div>
                      <div className="image-watermark">Shadi Arts ©</div>
                    </div>
                  </div>
                ) : (
                  <div className="modal-placeholder shimmer">
                    <div className="modal-preview">
                      <div className="modal-stroke stroke-1"></div>
                      <div className="modal-stroke stroke-2"></div>
                      <div className="modal-stroke stroke-3"></div>
                      <div className="modal-stroke stroke-4"></div>
                    </div>
                    <div className="image-watermark">Shadi Arts ©</div>
                  </div>
                )}
              </div>

              <div className="modal-details">
                <h3 className="modal-title">
                  {language === "ar"
                    ? selectedArtwork.arabicTitle || selectedArtwork.title
                    : selectedArtwork.title}
                </h3>
                <div className="modal-category floating">
                  {getTranslatedCategory(selectedArtwork.category)}
                </div>
                <p className="modal-description">
                  {language === "ar"
                    ? selectedArtwork.arabicDescription ||
                      selectedArtwork.description
                    : selectedArtwork.description}
                </p>

                <div className="modal-specs">
                  <div className="spec">
                    <span className="spec-label">
                      {t("Year of Production", "سنة الانتاج")} :
                    </span>
                    <span className="spec-value">{selectedArtwork.year}</span>
                  </div>
                  <div className="spec">
                    <span className="spec-label">
                      {t("Paper Size ", "حجم الورقة")} :
                    </span>
                    <span className="spec-value">{selectedArtwork.size}</span>
                  </div>
                  <div className="spec">
                    <span className="spec-label">
                      {" "}
                      {t("Category", "الفئة")} :
                    </span>
                    <span className="spec-value">
                      {" "}
                      {selectedArtwork.category}{" "}
                    </span>
                  </div>
                  <div className="spec">
                    <span className="spec-label">
                      {" "}
                      {t("Time", "الوقت المستغرق")}:
                    </span>
                    <span className="spec-value">4-10 hours</span>
                  </div>
                </div>

                <div className="modal-tags">
                  {selectedArtwork.tags.map((tag) => (
                    <span key={tag} className="modal-tag floating">
                      {tag}
                    </span>
                  ))}
                </div>

                {/* 🆕 أزرار الفيديو (تظهر فقط لو فيه فيديو) */}
                {(selectedArtwork.longVideoId ||
                  selectedArtwork.shortVideoId) && (
                  <div className="modal-video-buttons">
                    {selectedArtwork.longVideoId && (
                      <button
                        className="video-btn long-video-btn"
                        onClick={handleWatchLongVideo}
                      >
                        🎬{" "}
                        {t(
                          "Watch Full Video",
                          "شاهد الفيديو الكامل",
                        )}
                      </button>
                    )}

                    {selectedArtwork.shortVideoId && (
                      <button
                        className="video-btn short-video-btn"
                        onClick={handleWatchShortVideo}
                      >
                        ⚡{" "}
                        {t("Watch Short Video", "شاهد الفيديو القصير")}
                      </button>
                    )}
                  </div>
                )}

                <div className="modal-actions">
                  <button
                    className="modal-btn primary floating"
                    onClick={handleOrderSimilar}
                  >
                    🛒 {t("Order Similar Portrait", "اطلب هذه اللوحة")}
                  </button>

                  <button
                    className="modal-btn secondary"
                    onClick={handleCustomOrder}
                  >
                    📩{" "}
                    {t(
                      "Contact for Custom Work",
                      "تواصل معي لطلب عمل فني مخصص",
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;